/**
 * ResizeObserver error handler and browser specific fixes
 * Aggressively suppresses ResizeObserver errors across all browsers
 */

(function() {
  // Only run this in the browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  // APPROACH 1: Override error handling at the console level
  // This is the most reliable approach for suppressing the error messages
  const originalConsoleError = console.error;
  console.error = function(...args) {
    // Filter out ResizeObserver errors specifically
    if (args.length > 0 && 
        typeof args[0] === 'string' && 
        (args[0].includes('ResizeObserver loop') || 
         args[0].includes('ResizeObserver was not able'))) {
      // Completely suppress these errors
      return;
    }
    // Pass through other errors
    return originalConsoleError.apply(console, args);
  };

  // APPROACH 2: Handle window errors directly
  window.addEventListener('error', function(e) {
    if (e && e.message && 
        (e.message.includes('ResizeObserver loop') || 
         e.message.includes('ResizeObserver was not able'))) {
      // Stop the error from propagating
      e.stopImmediatePropagation();
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  }, true);

  // APPROACH 3: Replace ResizeObserver completely if it exists
  if (window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;

    // Function to limit observation frequency
    const debounce = (fn, delay = 30) => {
      let timer;
      return function(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    };

    // Create a safer observer implementation
    window.ResizeObserver = class SafeResizeObserver extends OriginalResizeObserver {
      constructor(callback) {
        // Process observations in a safer way
        const safeCallback = debounce(function(entries, observer) {
          // Skip empty entries
          if (!entries || !entries.length) return;
          
          // Process each entry to make sure it's valid
          const validEntries = entries.filter(entry => {
            try {
              // Verify that the target is still in the document
              // This helps avoid errors when elements are removed
              return entry && entry.target && document.contains(entry.target);
            } catch (e) {
              return false;
            }
          });
          
          // Only call the callback if we have valid entries
          if (validEntries.length > 0) {
            try {
              callback.call(this, validEntries, observer);
            } catch (e) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('Error in ResizeObserver callback (suppressed):', e);
              }
            }
          }
        }, 30);
        
        super(safeCallback);
      }
    };
  }

  // APPROACH 4: For React routing issues, add a cleanup on navigation
  const cleanupObservers = () => {
    // Use a brief delay to let React finish its rendering cycle
    setTimeout(() => {
      // Force garbage collection of any lingering observers
      if (window.gc) window.gc();
    }, 100);
  };

  // Try to detect route changes (if available)
  if (typeof window.history !== 'undefined') {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    // Override pushState to detect navigation
    window.history.pushState = function() {
      const result = originalPushState.apply(this, arguments);
      cleanupObservers();
      return result;
    };
    
    // Override replaceState to detect navigation
    window.history.replaceState = function() {
      const result = originalReplaceState.apply(this, arguments);
      cleanupObservers();
      return result;
    };
    
    // Also listen for popstate events (browser back/forward)
    window.addEventListener('popstate', cleanupObservers);
  }

  // Log that the fixes have been applied
  if (process.env.NODE_ENV === 'development') {
    console.info('Enhanced ResizeObserver protection activated');
  }
})();
